// store/modules/tabs.ts
export interface Tab {
  id: string
  title: string
  route: string
  icon?: string
  closable: boolean
  componentName?: string
}

export interface TabsState {
  tabs: Tab[]
  activeTab: string
  cachedViews: string[]
}

// Cargar desde localStorage
function loadFromStorage(): TabsState {
  try {
    const saved = localStorage.getItem('tabs-state')
    if (saved) {
      const parsed = JSON.parse(saved)
      return {
        tabs: parsed.tabs || [getDefaultDashboardTab()],
        activeTab: parsed.activeTab || 'dashboard',
        cachedViews: parsed.cachedViews || ['Dashboard']
      }
    }
  } catch (error) {
    console.error('Error loading tabs from storage:', error)
  }
  
  return {
    tabs: [getDefaultDashboardTab()],
    activeTab: 'dashboard',
    cachedViews: ['Dashboard']
  }
}

// Tab por defecto del dashboard
function getDefaultDashboardTab(): Tab {
  return {
    id: 'dashboard',
    title: 'Dashboard',
    route: '/dashboard',
    icon: 'mdi-view-dashboard',
    closable: false,
    componentName: 'Dashboard'
  }
}

// Guardar en localStorage
function saveToStorage(state: TabsState) {
  try {
    localStorage.setItem('tabs-state', JSON.stringify({
      tabs: state.tabs,
      activeTab: state.activeTab,
      cachedViews: state.cachedViews
    }))
  } catch (error) {
    console.error('Error saving tabs to storage:', error)
  }
}

const state: TabsState = loadFromStorage()

const mutations = {
  ADD_TAB(state: TabsState, tab: Tab) {
    const exists = state.tabs.find(t => t.id === tab.id)
    if (!exists) {
      state.tabs.push(tab)
      if (tab.componentName && !state.cachedViews.includes(tab.componentName)) {
        state.cachedViews.push(tab.componentName)
      }
    }
    state.activeTab = tab.id
    saveToStorage(state)
  },
  
  REMOVE_TAB(state: TabsState, tabId: string) {
    const index = state.tabs.findIndex(t => t.id === tabId)
    if (index > -1) {
      const tab = state.tabs[index]
      state.tabs.splice(index, 1)
      
      if (tab.componentName) {
        const cacheIndex = state.cachedViews.indexOf(tab.componentName)
        if (cacheIndex > -1) {
          state.cachedViews.splice(cacheIndex, 1)
        }
      }
      
      if (state.activeTab === tabId) {
        const newIndex = Math.max(0, index - 1)
        state.activeTab = state.tabs[newIndex]?.id || 'dashboard'
      }
    }
    saveToStorage(state)
  },
  
  SET_ACTIVE_TAB(state: TabsState, tabId: string) {
    state.activeTab = tabId
    saveToStorage(state)
  },
  
  CLEAR_TABS(state: TabsState) {
    state.tabs = [getDefaultDashboardTab()]
    state.activeTab = 'dashboard'
    state.cachedViews = ['Dashboard']
    saveToStorage(state)
  },

  RESTORE_TABS(state: TabsState, savedState: TabsState) {
    state.tabs = savedState.tabs
    state.activeTab = savedState.activeTab
    state.cachedViews = savedState.cachedViews
  }
}

const actions = {
  openTab({ commit, state }: any, tab: Tab) {
    commit('ADD_TAB', tab)
  },
  
  closeTab({ commit, state }: any, tabId: string) {
    const tab = state.tabs.find((t: Tab) => t.id === tabId)
    if (tab && tab.closable) {
      commit('REMOVE_TAB', tabId)
    }
  },
  
  setActiveTab({ commit }: any, tabId: string) {
    commit('SET_ACTIVE_TAB', tabId)
  },

  clearTabs({ commit }: any) {
    commit('CLEAR_TABS')
  },

  restoreTabs({ commit }: any) {
    const savedState = loadFromStorage()
    commit('RESTORE_TABS', savedState)
  }
}

const getters = {
  tabs: (state: TabsState) => state.tabs,
  activeTab: (state: TabsState) => state.activeTab,
  cachedViews: (state: TabsState) => state.cachedViews,
  activeTabRoute: (state: TabsState) => {
    const tab = state.tabs.find(t => t.id === state.activeTab)
    return tab?.route || '/dashboard'
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}